import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import { getCart, removeFromCart, cartTotal, formatPrice } from "../lib/cart";
import { useLang } from "../lib/i18n";
import { getCartContent } from "../lib/cartContent";

export default function CartPage() {
  const nav = useNavigate();
  const { lang } = useLang();
  const cc = getCartContent(lang);
  const [items, setItems] = useState(getCart());
  const [address, setAddress] = useState("");
  const [addressError, setAddressError] = useState("");
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const fn = () => setItems(getCart());
    window.addEventListener("cart-updated", fn);

    // Check if user is logged in
    const token = localStorage.getItem("aabb_token");
    setIsLoggedIn(!!token);

    return () => window.removeEventListener("cart-updated", fn);
  }, []);

  // Load Google Places Autocomplete
  useEffect(() => {
    if (items.length === 0) return;

    // Note: Add REACT_APP_GOOGLE_PLACES_API_KEY to your .env file
    const googleApiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

    const loadGooglePlaces = () => {
      // Skip Google Places if no API key is configured
      if (!googleApiKey) {
        console.warn("Google Places API key not configured. Address validation will be manual.");
        return;
      }

      if (!window.google || !window.google.maps) {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initAutocomplete;
        document.head.appendChild(script);
      } else {
        initAutocomplete();
      }
    };

    const initAutocomplete = () => {
      if (!inputRef.current || !window.google) return;

      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ["address"],
        componentRestrictions: { country: "us" }
      });

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (place.formatted_address) {
          setAddress(place.formatted_address);
          setAddressError("");

          // Parse and store address components for checkout page
          if (place.address_components) {
            let streetNumber = "";
            let route = "";
            let city = "";
            let state = "";
            let postalCode = "";

            place.address_components.forEach((component) => {
              const types = component.types;
              if (types.includes("street_number")) {
                streetNumber = component.long_name;
              }
              if (types.includes("route")) {
                route = component.long_name;
              }
              if (types.includes("locality")) {
                city = component.long_name;
              }
              if (types.includes("administrative_area_level_1")) {
                state = component.short_name;
              }
              if (types.includes("postal_code")) {
                postalCode = component.long_name;
              }
            });

            const addressLine1 = `${streetNumber} ${route}`.trim();

            // Store parsed address components
            localStorage.setItem("delivery_address_parsed", JSON.stringify({
              address_line1: addressLine1,
              city: city,
              state: state,
              postal_code: postalCode
            }));
          }
        }
      });
    };

    loadGooglePlaces();
  }, [items.length]);

  const handleCheckout = () => {
    if (!address || address.trim().length === 0) {
      setAddressError(cc.addressError);
      return;
    }
    // Store address in localStorage for checkout page
    localStorage.setItem("delivery_address", address);
    nav("/checkout");
  };

  return (
    <>
      <TopNav />
      <div className="aa-shell">
        <h1 className="aa-h1" style={{textAlign: "center"}}>{cc.yourCart}</h1>
        {items.length === 0 ? (
          <div style={{textAlign: "center", maxWidth: "500px", marginInline: "auto"}}>
            <p className="aa-sub">{cc.cartEmpty}</p>
            <div style={{marginTop: "24px", display: "flex", justifyContent: "center"}}>
              <button className="aa-btn" onClick={()=>nav("/intake/start")} data-testid="cart-start-qualifying">{cc.startEligibility}</button>
            </div>
            {!isLoggedIn && (
              <div style={{marginTop: "24px"}}>
                <p className="aa-sub" style={{marginBottom: "12px"}}>{cc.alreadyHaveAccount}</p>
                <div style={{display: "flex", justifyContent: "center"}}>
                  <button className="aa-btn-ghost" onClick={()=>nav("/login")} data-testid="cart-signin">{cc.signIn}</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div data-testid="cart-items">
              {items.map(i => (
                <div key={i.sku} className="aa-card" style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10}}>
                  <div>
                    <div className="aa-card-title">{i.name}</div>
                    <div className="aa-card-sub">{cc.qty}: {i.qty}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:"var(--font-display)", fontSize:"1.3rem", color:"var(--gold)"}}>{formatPrice(i.price_cents * i.qty)}</div>
                    <button className="aa-btn-ghost" style={{padding:"6px 14px", fontSize:".75rem", marginTop:6}}
                            onClick={()=>removeFromCart(i.sku)} data-testid={`remove-${i.sku}`}>{cc.remove}</button>
                  </div>
                </div>
              ))}
            </div>
            <hr className="aa-hr"/>

            <h2 className="aa-h2" style={{fontSize:"1.2rem", marginTop: "20px"}}>{cc.deliveryAddress}</h2>
            <div style={{marginTop:10}}>
              <label className="aa-label">{cc.streetAddress}</label>
              <input
                ref={inputRef}
                className="aa-input"
                type="text"
                placeholder={cc.addressPlaceholder}
                value={address}
                onChange={(e)=>setAddress(e.target.value)}
                data-testid="cart-address"
              />
              {addressError && <div className="aa-error" style={{marginTop: 8}}>{addressError}</div>}
            </div>

            <hr className="aa-hr"/>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
              <span className="aa-label">{cc.total}</span>
              <span style={{fontFamily:"var(--font-display)", fontSize:"2rem", color:"var(--gold)"}} data-testid="cart-total">{formatPrice(cartTotal())}</span>
            </div>
            <div className="aa-bottom" style={{justifyContent: "center"}}>
              <button className="aa-btn" onClick={handleCheckout} data-testid="cart-checkout">{cc.proceedToCheckout}</button>
              <button className="aa-btn-ghost" onClick={()=>nav("/estore")} data-testid="cart-continue">{cc.continueShopping}</button>
            </div>
            {!isLoggedIn && (
              <div style={{marginTop: "20px", textAlign: "center"}}>
                <p className="aa-sub">{cc.alreadyHaveAccount}</p>
                <div style={{display: "flex", justifyContent: "center"}}>
                  <button className="aa-btn-ghost" onClick={()=>nav("/login")} data-testid="cart-signin-bottom">{cc.signIn}</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
